import { Fragment, memo } from "react"
import type { FC, ReactElement } from "react"

import { m, useReducedMotion } from "framer-motion"
import { useShallowCompareEffect } from "react-use"

import { idleCb, propsAreEqual } from "@/3514/utils"
import type { TEmptyCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
import { useCoachingChatConversationMessageByIdSelector as useMessageSelector } from "../store"
import { type TCoachingChatConversationMessage as TMessage } from "../types"

import { CoachingChatMessageLight as MessageLight } from "./coaching-chat.message-light.component"
import { CoachingChatMessageStreamable as MessageStreamable } from "./coaching-chat.message-streamable.component"
import { CoachingChatMessageFull as MessageFull } from "./coaching-chat.message.component"

type TProps = {
    clientMessageId: string
}

const { withModeMessage: withGuard } = guards

const MessageContainer: FC<TProps> = ({ clientMessageId }: TProps): ReactElement => {
    const isReducedMotion: boolean = useReducedMotion()

    const {
        entityId,
        wasCompleted: wasChatCompleted,
        conversation: { feedRef, feedScrollerHook }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    const state: TMessage | undefined = useMessageSelector(entityId, clientMessageId)

    useShallowCompareEffect((): void => {
        idleCb(scrollFeedToBottom, 20)
    }, [scrollFeedToBottom, state?.status])

    return wasChatCompleted ? (
        <Fragment>
            <MessageFull clientMessageId={clientMessageId} />
        </Fragment>
    ) : (
        <m.div
            layout="preserve-aspect"
            layoutId={clientMessageId}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
                layout: { duration: isReducedMotion ? 0 : 0.25, ease: [0.16, 1, 0.3, 1] },
                opacity: { duration: isReducedMotion ? 0 : 0.2 },
                y: { duration: isReducedMotion ? 0 : 0.25, ease: [0.16, 1, 0.3, 1] }
            }}
        >
            {state && (state.isStreamable ? <MessageStreamable /> : <MessageFull clientMessageId={clientMessageId} />)}
        </m.div>
    )
}

const GuardedMessage: FC<TProps> = withGuard<TProps>(MessageContainer, MessageLight)

const MemoizedGuardedMessage: FC<TProps> = memo(GuardedMessage, propsAreEqual)

MemoizedGuardedMessage.displayName = "CoachingChatMessageContainer"

export { MemoizedGuardedMessage as CoachingChatMessageContainer }
